import { setFormsQuery, useFormsQuery } from '../../lib/Queries';
import Loading from '../Loading';
import React from 'react';
import useBusinessNavigate, {
    getBusinessUrl,
} from '../../lib/useBusinessNavigate.js';
import useDelete from '../../lib/useDelete.jsx';
import { Link } from 'react-router-dom';
import DotDotDotMenu from '../DotDotDotMenu';
import PencilSquareIcon from '../icons/PencilSquareIcon';
import CopyIcon from '../icons/CopyIcon.jsx';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import {
    CalendarIcon,
    DocumentCheckIcon,
    DocumentDuplicateIcon,
    EyeIcon,
    EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { ucallwords } from '~/lib/Utils.js';

const FormRow = ({ form }) => {
    const navigate = useBusinessNavigate();
    const { saveDelete, isDeleting } = useDelete(`/forms/${form.id}/delete`);

    const canDelete = !form.is_default_booking;

    const { save: saveDuplicate, isSaving: isDuplicating } = useBusinessPostApi(
        `/forms/${form.id}/duplicate`,
    );
    const { save: saveMakeDefault, isSaving: isMakingDefault } =
        useBusinessPostApi(`/forms/${form.id}/make-default`);
    const { save: saveDisable, isSaving: isDisabling } = useBusinessPostApi(
        `/forms/${form.id}/disable`,
    );
    const { save: saveEnable, isSaving: isEnabling } = useBusinessPostApi(
        `/forms/${form.id}/enable`,
    );

    function doDelete() {
        if (!canDelete) {
            return;
        }
        saveDelete().then(result => {
            setFormsQuery(result);
        });
    }

    function doDuplicate() {
        saveDuplicate().then(result => {
            navigate(`/forms/${result.form.id}`);
        });
    }

    function doMakeDefault() {
        saveMakeDefault().then(result => {
            setFormsQuery(result);
        });
    }

    function doDisable() {
        saveDisable().then(result => {
            setFormsQuery(result);
        });
    }

    function doEnable() {
        saveEnable().then(result => {
            setFormsQuery(result);
        });
    }

    const menuOptions = [
        {
            name: 'Edit',
            icon: <PencilSquareIcon />,
            onClick: () => {
                navigate(`/forms/${form.id}`);
            },
        },
        {
            name: isDuplicating ? 'Duplicating...' : 'Duplicate',
            icon: <CopyIcon />,
            onClick: doDuplicate,
        },
        {
            name: 'Share',
            icon: <CopyIcon />,
            onClick: () => {
                navigate(`/forms/${form.id}/share`);
            },
            className: 'sm:hidden',
        }
    ];

    if (!form.is_default_booking && form.is_booking) {
        menuOptions.push({
            name: isMakingDefault ? 'Making Default...' : 'Make Default',
            icon: <DocumentCheckIcon />,
            onClick: doMakeDefault,
        });
    }

    // Add enable/disable option
    if (form.disabled) {
        menuOptions.push({
            name: isEnabling ? 'Enabling...' : 'Enable Form',
            icon: <EyeIcon />,
            onClick: doEnable,
        });
    } else {
        // Only allow disabling if it's not the default booking form
        if (!form.is_default_booking) {
            menuOptions.push({
                name: isDisabling ? 'Disabling...' : 'Disable Form',
                icon: <EyeSlashIcon />,
                onClick: doDisable,
            });
        }
    }

    if (canDelete) {
        menuOptions.push({
            name: isDeleting ? 'Deleting...' : 'Delete',
            isDelete: true,
            onClick: doDelete,
        });
    }

    return (
        <div
            key={form.id}
            className="py-4 pr-6 flex items-center justify-between"
        >
            <Link
                to={getBusinessUrl(`/forms/${form.id}`)}
                className={`flex-1 flex flex-col sm:flex-row sm:gap-2 text-lg no-underline ${
                    form.disabled ? 'text-gray-400' : 'text-black'
                }`}
            >
                {form.name || 'Unnamed Form'}
                {form.is_default_booking && (
                    <div className="text-primary-700 text-xs uppercase font-medium">
                        Default booking form
                    </div>
                )}
                {form.disabled && (
                    <div className="flex items-center">
                        <div className="tag bg-gray-500 ml-4 mr-2 leading-tight">
                            Disabled
                        </div>
                    </div>
                )}
            </Link>

            <Link
                to={getBusinessUrl(`/forms/${form.id}/share`)}
                className="hidden sm:flex text-black text-lg no-underline btn-light-pill-sm uppercase mr-4"
            >
                Share
            </Link>

            <DotDotDotMenu options={menuOptions} />
        </div>
    );
};

const FormList = ({}) => {
    const { isLoading, data } = useFormsQuery();
    const forms = data || [];

    // Group forms by capabilities so we can show those as headings in the list
    const formsByCapabilities = Object.groupBy(forms, form => {
        return forms.length > 10 ? (form.capabilities || []).join(',') : '';
    });

    return (
        <div>
            {isLoading && <Loading className="p-6" />}

            {Object.keys(formsByCapabilities)
                .sort()
                .map(capabilities => {
                    const forms = formsByCapabilities[capabilities];
                    return (
                        <div key={capabilities} className="mb-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl px-6 mb-2">
                                    {ucallwords(capabilities)}
                                </h3>
                            </div>
                            <div className="panel pl-4 divide-y">
                                {forms.map(form => (
                                    <FormRow key={form.id} form={form} />
                                ))}
                            </div>
                        </div>
                    );
                })}

            {/*{forms.length > 0 && (*/}
            {/*    <div className="ml-6 divide-y">*/}
            {/*        {forms.map(form => (*/}
            {/*            <FormRow key={form.id} form={form} />*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*)}*/}

            {forms.length === 0 && !isLoading && (
                <div className="p-4 panel">
                    You don't have any custom forms. Create a form to collect
                    information from clients.
                </div>
            )}
        </div>
    );
};

export default FormList;
